import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { 
  Add as AddIcon, 
  CloudUpload as UploadIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon,
  CheckCircle as KeepIcon,
  Storage as StoreIcon,
  AttachMoney as SellIcon,
  DeleteForever as DiscardIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Furniture = () => {
  const navigate = useNavigate();
  const [furniture, setFurniture] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [updating, setUpdating] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    condition: '',
    description: '',
    seats: '',
    cushion_type: '',
    material: '',
    length: '',
    width: '',
    height: '',
    sale_price: '',
    rental_cost: '',
    new_cost: '',
    refurnishment_cost: '',
  });
  const [images, setImages] = useState([]);

  useEffect(() => {
    fetchFurniture();
    // Check if we're editing from detail page
    const urlParams = new URLSearchParams(window.location.search);
    const editId = urlParams.get('edit');
    if (editId) {
      handleEdit(editId);
      // Clean up URL
      window.history.replaceState({}, '', '/furniture');
    }
  }, []);

  const fetchFurniture = async () => {
    try {
      const response = await axios.get(`${API_URL}/furniture`);
      const items = response.data.furniture || [];
      
      // Parse recommendations for each item
      const parsedItems = items.map(item => {
        if (item.recommendation && typeof item.recommendation === 'string') {
          try {
            item.recommendation = JSON.parse(item.recommendation);
          } catch (e) {
            item.recommendation = {};
          }
        }
        // Ensure numeric fields are numbers
        if (item.volume) item.volume = parseFloat(item.volume);
        if (item.estimated_value) item.estimated_value = parseFloat(item.estimated_value);
        if (item.sale_price) item.sale_price = parseFloat(item.sale_price);
        if (item.rental_cost) item.rental_cost = parseFloat(item.rental_cost);
        if (item.new_cost) item.new_cost = parseFloat(item.new_cost);
        if (item.refurnishment_cost) item.refurnishment_cost = parseFloat(item.refurnishment_cost);
        return item;
      });
      
      setFurniture(parsedItems);
    } catch (error) {
      console.error('Error fetching furniture:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    setImages(Array.from(e.target.files));
  };

  const calculateVolume = () => {
    if (formData.length && formData.width && formData.height) {
      return (parseFloat(formData.length) * parseFloat(formData.width) * parseFloat(formData.height)).toFixed(2);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      if (editingId) {
        await axios.put(`${API_URL}/furniture/${editingId}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${API_URL}/furniture`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      handleClose();
      fetchFurniture();
    } catch (error) {
      console.error('Error saving furniture item:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setActiveStep(0);
    setFormData({
      name: '',
      category: '',
      condition: '',
      description: '',
      seats: '',
      cushion_type: '',
      material: '',
      length: '',
      width: '',
      height: '',
      sale_price: '',
      rental_cost: '',
      new_cost: '',
      refurnishment_cost: '',
    });
    setImages([]);
  };

  const handleEdit = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/furniture/${id}`);
      const item = response.data.furniture;
      setFormData({
        name: item.name || '',
        category: item.category || '',
        condition: item.condition || '',
        description: item.description || '',
        seats: item.seats || '',
        cushion_type: item.cushion_type || '',
        material: item.material || '',
        length: item.length || '',
        width: item.width || '',
        height: item.height || '',
        sale_price: item.sale_price || '',
        rental_cost: item.rental_cost || '',
        new_cost: item.new_cost || '',
        refurnishment_cost: item.refurnishment_cost || '',
      });
      setEditingId(id);
      setOpen(true);
    } catch (error) {
      console.error('Error fetching furniture item:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this furniture item?')) {
      try {
        await axios.delete(`${API_URL}/furniture/${id}`);
        fetchFurniture();
      } catch (error) {
        console.error('Error deleting furniture item:', error);
        alert('Failed to delete furniture item');
      }
    }
  };

  const handleDecisionChange = async (itemId, newAction) => {
    setUpdating({ ...updating, [itemId]: true });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/furniture/${itemId}/decision`, 
        { action: newAction },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // Update local state
      setFurniture(prevFurniture =>
        prevFurniture.map(item =>
          item.id === itemId ? { ...item, action: newAction } : item
        )
      );
      console.log('Decision updated successfully:', response.data);
    } catch (error) {
      console.error('Error updating decision:', error);
      alert(`Failed to update decision: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setUpdating({ ...updating, [itemId]: false });
    }
  };

  const getActionColor = (action) => {
    const colors = {
      keep: 'success',
      store: 'info',
      sell: 'warning',
      discard: 'error',
    };
    return colors[action] || 'default';
  };

  const steps = ['Basic Information', 'Details & Dimensions', 'Cost Information'];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
              >
                <MenuItem value="sofa">Sofa</MenuItem>
                <MenuItem value="bed">Bed</MenuItem>
                <MenuItem value="dining table">Dining Table</MenuItem>
                <MenuItem value="wardrobe">Wardrobe</MenuItem>
                <MenuItem value="chair">Chair</MenuItem>
                <MenuItem value="table">Table</MenuItem>
                <MenuItem value="cabinet">Cabinet</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Condition</InputLabel>
              <Select
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                label="Condition"
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="good">Good</MenuItem>
                <MenuItem value="fair">Fair</MenuItem>
                <MenuItem value="poor">Poor</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              margin="normal"
            />
          </>
        );
      case 1:
        return (
          <>
            {formData.category === 'sofa' && (
              <>
                <TextField
                  fullWidth
                  label="Number of Seats"
                  name="seats"
                  type="number"
                  value={formData.seats}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 1 }}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Cushion Type</InputLabel>
                  <Select
                    name="cushion_type"
                    value={formData.cushion_type}
                    onChange={handleChange}
                    label="Cushion Type"
                  >
                    <MenuItem value="removable">Removable</MenuItem>
                    <MenuItem value="fixed">Fixed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Cushion Material</InputLabel>
                  <Select
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    label="Cushion Material"
                  >
                    <MenuItem value="fabric">Fabric</MenuItem>
                    <MenuItem value="leather">Leather</MenuItem>
                    <MenuItem value="synthetic">Synthetic</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Dimensions (in feet)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Length"
                  name="length"
                  type="number"
                  value={formData.length}
                  onChange={handleChange}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Width"
                  name="width"
                  type="number"
                  value={formData.width}
                  onChange={handleChange}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Height"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleChange}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
            </Grid>
            {calculateVolume() && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Calculated Volume: {calculateVolume()} cubic feet
              </Alert>
            )}
            <Box mt={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Upload Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {images.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {images.length} file(s) selected
                </Typography>
              )}
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide cost information to help with budget planning
            </Typography>
            <TextField
              fullWidth
              label="Potential Sale Price (₹)"
              name="sale_price"
              type="number"
              value={formData.sale_price}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Estimated value if you sell this item"
            />
            <TextField
              fullWidth
              label="Cost of Rental (₹)"
              name="rental_cost"
              type="number"
              value={formData.rental_cost}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Cost to rent a similar item during redevelopment"
            />
            <TextField
              fullWidth
              label="Cost of New Item (₹)"
              name="new_cost"
              type="number"
              value={formData.new_cost}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Cost to buy a new similar item"
            />
            <TextField
              fullWidth
              label="Refurnishment Cost (₹)"
              name="refurnishment_cost"
              type="number"
              value={formData.refurnishment_cost}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Cost to refurbish/repair this item"
            />
          </>
        );
      default:
        return null;
    }
  };

  // Calculate decision summary
  const decisionCounts = furniture.reduce((acc, item) => {
    const action = item.action || 'pending';
    acc[action] = (acc[action] || 0) + 1;
    return acc;
  }, {});

  const decisionGroups = [
    { key: 'keep', label: 'Keep', color: 'success', icon: <KeepIcon /> },
    { key: 'store', label: 'Store', color: 'info', icon: <StoreIcon /> },
    { key: 'sell', label: 'Sell', color: 'warning', icon: <SellIcon /> },
    { key: 'discard', label: 'Discard', color: 'error', icon: <DiscardIcon /> },
    { key: 'pending', label: 'Pending', color: 'default', icon: null },
  ];

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          My Furniture
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Furniture
        </Button>
      </Box>

      {/* Decision Summary Cards */}
      {furniture.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Decision Summary
          </Typography>
          <Grid container spacing={2}>
            {decisionGroups.map((group) => {
              const count = decisionCounts[group.key] || 0;
              return (
                <Grid item xs={6} sm={4} md={2.4} key={group.key}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        {group.icon && (
                          <Box sx={{ color: `${group.color}.main` }}>
                            {group.icon}
                          </Box>
                        )}
                      </Box>
                      <Typography variant="h4" color={`${group.color}.main`} gutterBottom>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {furniture.length === 0 ? (
        <Alert severity="info">No furniture items yet. Add your first item to get started!</Alert>
      ) : (
        <Grid container spacing={3}>
          {furniture.map((item) => {
            const recommendedAction = item.recommendation?.defaultAction || 
              (item.recommendation?.keep?.length > 0 ? 'keep' :
               item.recommendation?.store?.length > 0 ? 'store' :
               item.recommendation?.sell?.length > 0 ? 'sell' :
               item.recommendation?.discard?.length > 0 ? 'discard' : null);
            const currentAction = item.action || 'pending';

            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {item.name}
                    </Typography>
                    <Box mb={1}>
                      <Chip label={item.category} size="small" sx={{ mr: 1 }} />
                      <Chip label={item.condition} size="small" sx={{ mr: 1 }} />
                      {item.action && (
                        <Chip
                          label={item.action}
                          size="small"
                          color={getActionColor(item.action)}
                        />
                      )}
                    </Box>
                    {item.estimated_value && (
                      <Typography variant="body2" color="text.secondary">
                        Estimated Value: ₹{item.estimated_value.toLocaleString()}
                      </Typography>
                    )}
                    {item.volume && (
                      <Typography variant="body2" color="text.secondary">
                        Volume: {item.volume.toFixed(2)} cu ft
                      </Typography>
                    )}
                    {item.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {item.description}
                      </Typography>
                    )}
                    
                    {/* Recommendation Display */}
                    {recommendedAction && recommendedAction !== currentAction && (
                      <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Recommended: <Chip 
                            label={recommendedAction} 
                            size="small" 
                            color={getActionColor(recommendedAction)}
                            sx={{ ml: 0.5 }}
                          />
                        </Typography>
                      </Box>
                    )}

                    {/* Decision Dropdown */}
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Decision</InputLabel>
                        <Select
                          value={currentAction}
                          label="Decision"
                          onChange={(e) => handleDecisionChange(item.id, e.target.value)}
                          disabled={updating[item.id]}
                        >
                          <MenuItem value="keep">
                            <Box display="flex" alignItems="center" gap={1}>
                              <KeepIcon fontSize="small" color="success" />
                              Keep
                            </Box>
                          </MenuItem>
                          <MenuItem value="store">
                            <Box display="flex" alignItems="center" gap={1}>
                              <StoreIcon fontSize="small" color="info" />
                              Store
                            </Box>
                          </MenuItem>
                          <MenuItem value="sell">
                            <Box display="flex" alignItems="center" gap={1}>
                              <SellIcon fontSize="small" color="warning" />
                              Sell
                            </Box>
                          </MenuItem>
                          <MenuItem value="discard">
                            <Box display="flex" alignItems="center" gap={1}>
                              <DiscardIcon fontSize="small" color="error" />
                              Discard
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate(`/furniture/${item.id}`)}>
                      View Details
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<EditIcon />} 
                      onClick={() => handleEdit(item.id)}
                    >
                      Edit
                    </Button>
                    <Button 
                      size="small" 
                      color="error" 
                      startIcon={<DeleteIcon />} 
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Furniture Item' : 'Add Furniture Item'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {renderStepContent(activeStep)}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            {activeStep > 0 && (
              <Button onClick={() => setActiveStep(activeStep - 1)}>
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={() => setActiveStep(activeStep + 1)}>
                Next
              </Button>
            ) : (
              <Button type="submit" variant="contained">
                {editingId ? 'Update' : 'Add'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Furniture;
